import {compact} from '@benchling/js-utils';
import {css, StyleSheet, StyleDeclarationValue} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {
  Handle,
  Position,
  useUpdateNodeInternals,
  useStore as useReactFlowStore,
  NodeProps,
  useReactFlow,
} from 'reactflow';
import _ from 'underscore';

import {assert} from 'Asserts';
import {FlowchartActionFunctionButtonToolbar} from 'processes/graph-builder/flowchart-viz/FlowchartActionFunctionButtonToolbar';
import {
  NODE_TYPES_WITH_SOURCE_HANDLE,
  NODE_TYPES_WITH_TARGET_HANDLE,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import ProcessFlowchartBaseNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartBaseNodeRenderer';
import {
  ProcessFlowchartHandleClassName,
  ProcessFlowchartInteractiveNodeWithActionFunctions,
  ProcessFlowchartNodeType,
  interactiveNodeWrapperBorderWidth,
  sourceHandleWrapperPadding,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import ProcessNodeStatusCountDisplay from 'processes/graph-builder-processes/flowchart-viz/ProcessNodeStatusCountDisplay';
import {getNodeDefinition} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARKER, RED, WHITE} from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ProcessFlowchartContext from './ProcessFlowchartContext';

export interface ProcessFlowchartInteractiveNodeRendererProps extends NodeProps {
  data: ProcessFlowchartInteractiveNodeWithActionFunctions;
  type: string; // This value is passed thru ReactFlow, so we lose its provenance as an enum
  nodeStyles?: Array<StyleDeclarationValue>;
}

function hasTargetHandle(
  type: ProcessFlowchartNodeType,
  node: ProcessFlowchartInteractiveNodeWithActionFunctions
): boolean {
  return (
    NODE_TYPES_WITH_TARGET_HANDLE.includes(type) ||
    (type === ProcessFlowchartNodeType.DISCONNECTED_NODE && !node.locked)
  );
}

function hasSourceHandle(
  type: ProcessFlowchartNodeType,
  node: ProcessFlowchartInteractiveNodeWithActionFunctions
): boolean {
  return (
    NODE_TYPES_WITH_SOURCE_HANDLE.includes(type) ||
    (type === ProcessFlowchartNodeType.DISCONNECTED_NODE && !node.locked)
  );
}

function useNodeInternals(node: ProcessFlowchartInteractiveNodeWithActionFunctions): void {
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    // When we programatically update the node's handles we need to notify ReactFlow.
    // See https://reactflow.dev/docs/api/hooks/use-update-node-internals/
    updateNodeInternals(node.clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.incomingEdgeConnection?.incomingEdges.length]);
}

// Exported for test only
export function shouldShowNodeActionFunctions(
  node: ProcessFlowchartInteractiveNodeWithActionFunctions
): boolean {
  return !node.locked && node.actionFunctions != null && node.actionFunctions.length !== 0;
}

export default function ProcessFlowchartInteractiveNodeRenderer({
  data,
  type,
  nodeStyles,
  ...props
}: ProcessFlowchartInteractiveNodeRendererProps): JSX.Element {
  useNodeInternals(data);
  const sourceNodeIdForEdgeBeingCreated = useReactFlowStore((state) => state.connectionNodeId);
  const {getZoom} = useReactFlow();
  const [showSourceHandle, setShowSourceHandle] = useState(false);
  const [shouldShowEdgeDrop, setShouldShowEdgeDrop] = useState(false);
  const isPotentialTarget =
    sourceNodeIdForEdgeBeingCreated != null && sourceNodeIdForEdgeBeingCreated !== data.clientId;

  const {hideTooltips} = ProcessFlowchartContext.useContext();

  const errorMessage = (data.errors?.length ?? 0) > 0 ? _.unique(data.errors!).join('\n') : null;

  useEffect(() => {
    if (showSourceHandle && props.dragging) {
      setShowSourceHandle(false);
    }
  }, [props.dragging, showSourceHandle]);

  const sourceHandleClass = classNames(
    !data.locked && ProcessFlowchartHandleClassName.SOURCE,
    css(handleStyles.sourceHandle, (data.locked || !showSourceHandle) && handleStyles.hiddenSourceHandle)
  );
  const targetHandleClass = classNames(
    !data.locked && ProcessFlowchartHandleClassName.TARGET,
    css(handleStyles.targetHandle, (data.locked || !isPotentialTarget) && handleStyles.hiddenTargetHandle)
  );

  const nodeType = type as ProcessFlowchartNodeType;
  assert(nodeType in ProcessFlowchartNodeType, 'Unknown flowchart node type', {type, data});
  const errorTooltipStyling = css(getNodeDefinition(data.type).nodeStyles.errorStyle);

  return (
    <div
      className={css(styles.movableNodeWrapper)}
      {...getTestProps({
        component: 'GraphBuilderInteractiveNode',
        element: 'taskNode',
        key: data.name,
      })}
    >
      <div
        className={css(styles.showSourceHandleWrapper)}
        onMouseOver={() => {
          if (sourceNodeIdForEdgeBeingCreated == null) {
            setShowSourceHandle(true);
          }
        }}
        onMouseLeave={() => {
          setShowSourceHandle(false);
        }}
      >
        <Float
          interaction={FloatingInteraction.HOVER}
          placement={FloatingPlacement.RIGHT_START}
          floatingElementOptions={{
            floatingContentClassName: css(getNodeDefinition(data.type).nodeStyles.actionFunctionStyle),
            floatingContentStyles: {
              transform: `scale(${getZoom()}) translatey(${
                getZoom() * (data.instanceData.nameHeight ?? 0)
              }px`,
            },
          }}
          renderContent={() =>
            !hideTooltips &&
            shouldShowNodeActionFunctions(data) && (
              <FlowchartActionFunctionButtonToolbar
                className={css(styles.actionButtonWrapper)}
                actionFunctions={data.actionFunctions!}
                item={data}
              />
            )
          }
        >
          <ProcessFlowchartBaseNodeRenderer
            data={data}
            nodeStyles={compact([
              data.locked ? styles.lockedNode : styles.movableNode,
              errorMessage != null ? styles.errorNode : null,
              shouldShowEdgeDrop ? styles.canDropEdge : null,
              ...(nodeStyles ?? []),
            ])}
          >
            <ProcessNodeStatusCountDisplay statusCounts={data.statusCounts ?? []} />
            {hasTargetHandle(nodeType, data) && (
              <Handle
                type="target"
                position={Position.Top}
                className={targetHandleClass}
                onMouseOver={() => {
                  if (isPotentialTarget) {
                    setShouldShowEdgeDrop(true);
                  }
                }}
                onMouseLeave={() => {
                  setShouldShowEdgeDrop(false);
                }}
                {...getTestProps({
                  component: 'GraphBuilderInteractiveNode',
                  element: 'targetHandle',
                  key: data.name,
                })}
              />
            )}
            {hasSourceHandle(nodeType, data) && (
              <Handle
                type="source"
                position={Position.Bottom}
                className={sourceHandleClass}
                {...getTestProps({
                  component: 'GraphBuilderInteractiveNode',
                  element: 'sourceHandle',
                  key: data.name,
                })}
              />
            )}
            {errorMessage != null && (
              <div className={errorTooltipStyling}>
                <Tooltip text={errorMessage}>
                  <BenchlingIcon
                    icon="revision"
                    color={RED}
                    dataTestAttrs={{component: 'GraphBuilderInteractiveNode', element: 'errorIcon'}}
                  />
                </Tooltip>
              </div>
            )}
          </ProcessFlowchartBaseNodeRenderer>
        </Float>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  actionFunctionToolbarWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: 'fit-content',
  },
  actionButtonWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  errorNode: {
    border: `1px solid ${RED}`,
  },
  movableNodeWrapper: {
    cursor: 'move',
    // This creates a hidden area that acts as a "moat" -- a grabbable but transparent
    // wrapper around the node
    border: `${interactiveNodeWrapperBorderWidth}px solid transparent`,
    backgroundClip: 'padding-box',
  },
  showSourceHandleWrapper: {
    padding: sourceHandleWrapperPadding,
  },
  movableNode: {
    cursor: 'move',
  },
  lockedNode: {
    cursor: 'not-allowed',
  },
  canDropEdge: {
    boxShadow: `0 3px 10px 5px ${GRAY}`,
  },
});

const handleStyles = StyleSheet.create({
  sourceHandle: {
    position: 'absolute',
    bottom: -8,
    left: '50%',
    width: 16,
    height: 16,
    background: WHITE,
    border: `solid 1px ${GRAY_DARKER}`,
  },
  targetHandle: {
    background: 'transparent',
    borderRadius: 0,
    width: '100%',
    height: '100%',
    position: 'absolute',
    transform: undefined,
    border: undefined,
    opacity: 0,
  },
  hiddenTargetHandle: {
    zIndex: -1,
  },
  hiddenSourceHandle: {
    visibility: 'hidden',
  },
  dragHandle: {
    height: 32,
    width: 32,
    background: RED,
  },
});

/**
 * This map is passed to ReactFlow, which requires a map to determine which renderer should
 * be used for each node type.
 */
export const PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER = {
  [ProcessFlowchartNodeType.START_NODE]: ProcessFlowchartInteractiveNodeRenderer,
  [ProcessFlowchartNodeType.MIDDLE_NODE]: ProcessFlowchartInteractiveNodeRenderer,
  [ProcessFlowchartNodeType.END_NODE]: ProcessFlowchartInteractiveNodeRenderer,
  [ProcessFlowchartNodeType.DISCONNECTED_NODE]: ProcessFlowchartInteractiveNodeRenderer,
};
